import { SearchRounded } from "@mui/icons-material";
import {
  Box,
  IconButton,
  InputAdornment,
  Button,
  Dialog,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Input } from "../../controls";
import * as actions from "../../api/actions/pos";
import { controller, endpoints } from "../../api/actions/api";
import { useParams } from "react-router-dom";
import { safeOnComplete } from "../../utils/safeOnComplete";
import AddEditProd from "../products/AddEditProd";  // here add a new product in purchase form

const SearchBox = (props) => {
  const { t } = useTranslation();
  const {
    _getByObj,
    _getById,
    type = "product",
    _defStore,
    _setVal,
    handleCart,
    _inventoryList,
    setAlert,

  } = props;

  const [searchVal, setSearchVal] = useState("");
 
  const [showPosProductForm, setShowPosProductForm] = useState(false); // for Add product form 

  //Add a guard flag to prevent multiple Enter key handling
  const [enterHandled, setEnterHandled] = useState(false);
  
  const { id } = useParams();
  
  const normalizeToCartItem = (item) => {
    // Case 1: Already inventory + product
    if (item.product && item.product.id !== undefined) {
      return {
        inInventory: item.inInventory ?? 0,
        product: item.product,
      };
    }

    // Case 2: Inventory without product expanded
    if (item.productId !== undefined && item.product === undefined) {
      return {
        inInventory: item.inInventory ?? 0,
        product: {
          id: item.productId,
          name: item.name,
          sellPrice: item.sellPrice,
          tax: item.tax,
        },
      };
    }

    // Case 3: Product-only result
    return {
      inInventory: 0,
      product: item,
    };
  };

  const findExactBarcodeMatch = (value, products) =>
  products.find(p => p.barcode === value);

  const handleSearch = (e) => {
    const value = e.target.value;

    if (!value || e.key !== "Enter") return;
    if (enterHandled) return;

    setEnterHandled(true);

    setAlert({
      isOpen: true,
      type: "info",
      title: t("processing"),
      subTitle: t("searchingProduct"),
    });

    const onComplete = safeOnComplete(
      (res) => {
        if (
          res.status === 200 &&
          Array.isArray(res.result) &&
          res.result.length > 0
        ) {
          const exactMatch =
            findExactBarcodeMatch(value, res.result) ?? res.result[0];

          const cartItem = normalizeToCartItem(exactMatch);

          if (typeof handleCart === "function") {
            handleCart(cartItem);
          }
        } else {
          setAlert({
            isOpen: true,
            type: "warning",
            title: t("productNotFound"),
            subTitle: t("noProductFoundFor", { value }),
          });

          setShowPosProductForm(true);
        }

        setSearchVal("");
        setEnterHandled(false);
      },

      (err) => {
        setAlert({
          isOpen: true,
          type: "error",
          title: t("searchError"),
          subTitle: err?.message || t("requestFailed"),
        });

        setShowPosProductForm(true);
        setSearchVal("");
        setEnterHandled(false);
      }
    );

    getFromServer(value, onComplete);
  };

   const getFromServer = (value, onComplete) => {
    _getByObj(
      endpoints.Inventory +
        (type === "product"
          ? controller.FindProducts
          : controller.GetCatsByStore),
      { storeId: _defStore.id, value },
      actions.ActionType.SetSearchResults,
      onComplete
    );
  }; 

    const getRandomProd = React.useCallback(() => {  
    _getById(
      endpoints.Inventory + controller.GetProdsByStore,
      _defStore.id,
      null,
      (res) =>
        res.status === 200 &&  (type === "product") && // ✅ only set if type is product
        _setVal(
          { result: res.result },
          actions.ActionType.SetInventory
        )
    );
  }, [_defStore?.id, _getById, _setVal, type]);

   // Default POS items  
  const getDefPosItem = React.useCallback(() => {  
    const onComplete = safeOnComplete(
      (res) => {       
        const productCount = Array.isArray(res?.result) ? res.result.length : 0;

        if (res.status === 200 && productCount > 0 &&  (type === "product") ) // ✅ only set if type is product
          {
          _setVal(
            { result: res.result, productCount  },
            actions.ActionType.SetInventory
          );
        } else {
          getRandomProd();
        }
      },
      getRandomProd
    );

    _defStore.id &&
      _getById(
        endpoints.Inventory + controller.GetProdsByStore,
        _defStore.id,
        null,
        onComplete
      );
  }, [_defStore?.id, _getById, _setVal, getRandomProd, type]);

  const getCategories = React.useCallback(() => {  
    _defStore.id &&
      _getByObj(
        endpoints.Inventory + controller.GetCatsByStore,
        { storeId: _defStore.id },
        actions.ActionType.SetCategories
      );
  }, [_defStore?.id, _getByObj]);

  /**
   * Add new product button
   */
  const handleAddNewProduct = () => {
    setShowPosProductForm(true);
  };

  const didInit = React.useRef(false);

  /**
   * Initial load
   */
  useEffect(() => {
    document.getElementById("searchBox")?.focus();

    if (id !== undefined) {
      _getByObj(
        endpoints.Inventory + controller.GetProdsByCat,
        { storeId: _defStore.id, catId: id },
        actions.ActionType.SetInventory
      );
      return;
    } 
  
     // ✅ ONLY load POS default if list is empty
    if (type === "product" &&
        !didInit.current &&
         (!_inventoryList || _inventoryList.length === 0)
      ) {                       //(!_list || _list.length === 0)) {
      didInit.current = true; // ✅ set flag to prevent future auto-loads
      getDefPosItem();
    }

    if (type === "categories") {
      getCategories();
    }

    return () => {
      // ❌ DO NOT clear global POS state
      // _setVal();
    };

  }, [
    _defStore?.id,
    id,
    type,
    _inventoryList,
    _getByObj,
    getCategories,
    getDefPosItem
  ]); 

  return (
    <>
      {/* 🔍 Search box + Add button */}
      <Box
        className="shadow-xs mb-30"
        sx={{
          display: "flex",
          alignItems: "stretch",
          borderRadius: "10px",
          overflow: "hidden",
        }}
      >
        <Input
          label={t("enterNameOrScanBarcode")}
          id="searchBox"
          value={searchVal}
          onChange={(e) => setSearchVal(e.target.value)}
          onKeyDown={handleSearch}
          autoFocus
          fullWidth
          sx={{
            flex: 1,
            " .MuiFilledInput-root": {
              backgroundColor: "#fff !important",
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton>
                  <SearchRounded />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          variant="contained"
          color="primary"
          onClick={handleAddNewProduct}
          sx={{ borderRadius: 0, px: 3 }}
        >
          {t("addNewProduct")}
        </Button>
      </Box>

{/*count > 0 && <span>{count} products available</span>*/}

      <span> {t("productsAvailable", {count: _inventoryList?.length || 0, })}</span>

      {/* 🧾 Add Product Modal (POS best UX) */}
      {showPosProductForm  && (
        <Dialog open={showPosProductForm } maxWidth="md" fullWidth>
          <AddEditProd
            from="pos"
            initialBarcode={searchVal}
            setShowPurchForm={setShowPosProductForm }
            onSaved={(payload) => {
              setShowPosProductForm(false);

              if (payload?.type === "inventory-update") {
                // 🔥 refresh POS grid correctly
                getRandomProd(); // or getDefPosItem()
                return;
              }

              // normal product add
              setTimeout(() => {
                handleCart(payload);
              }, 0);
            }}

          />
        </Dialog>
      )}

    </>
  );
};

const mapStateToProps = (state) => ({
  _defStore: state.pos.defStore,
  _inventoryList: state.pos.inventoryList,
  _categoriesList: state.pos.categoriesList,
  _searchResults: state.pos.searchResults,
});

const mapActionToProps = {
  _getByObj: actions.getByObj,
  _getById: actions.getById,
  _setVal: actions.setVal,
};

export default connect(mapStateToProps, mapActionToProps)(SearchBox);